import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { IMPACT_LABELS, PROBABILITY_LABELS, REVIEW_PERIODS, RISK_CATEGORIES, emptyMeasure, emptyRiskForm, governanceError, newKey, riskPayload, riskProblem } from "../lib/governance";
import { LinkList, LinkPicker, PeopleMultiSelect } from "../components/governance/GovernanceBits";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Skeleton } from "../components/ui/skeleton";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

function Section({ title, hint, children }) {
  return (
    <Card className="p-4 sm:p-5 space-y-3">
      <div><h2 className="text-base font-semibold text-slate-900">{title}</h2>{hint && <p className="text-sm text-slate-500">{hint}</p>}</div>
      {children}
    </Card>
  );
}

function ScaleSelect({ label, labels, value, onChange, disabled, testid }) {
  return (
    <div className="space-y-1.5"><Label>{label}</Label>
      <Select value={value ? String(value) : undefined} onValueChange={(v) => onChange(Number(v))} disabled={disabled}>
        <SelectTrigger className="h-10" aria-label={label} data-testid={testid}><SelectValue placeholder="Évaluer" /></SelectTrigger>
        <SelectContent>{Object.entries(labels).map(([n, text]) => <SelectItem key={n} value={n}>{`${n} — ${text}`}</SelectItem>)}</SelectContent>
      </Select></div>
  );
}

export default function GovernanceRiskForm() {
  const { riskId } = useParams();
  const editing = !!riskId;
  const navigate = useNavigate();
  const [form, setForm] = useState(() => emptyRiskForm());
  const [source, setSource] = useState(null);
  const [owners, setOwners] = useState([]);
  const [decisions, setDecisions] = useState([]);
  const [phase, setPhase] = useState(editing ? "loading" : "ready");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [key, setKey] = useState(newKey);
  const locked = source?.status === "closed";

  useEffect(() => {
    let alive = true;
    api.get("/governance/risks/owner-options").then(({ data }) => { if (alive) setOwners(data.items || []); }).catch(() => {});
    api.get("/governance/decisions", { params: { view: "all", limit: 100 } }).then(({ data }) => { if (alive) setDecisions((data.items || []).map((d) => ({ id: d.id, name: `${d.number} — ${d.title}` }))); }).catch(() => {});
    return () => { alive = false; };
  }, []);
  useEffect(() => {
    if (!riskId) return undefined;
    let alive = true;
    api.get(`/governance/risks/${riskId}`).then(({ data }) => { if (alive) { setSource(data); setForm(emptyRiskForm(data)); setPhase("ready"); } }).catch(() => { if (alive) setPhase("error"); });
    return () => { alive = false; };
  }, [riskId]);

  const set = (patch) => { setForm((f) => ({ ...f, ...patch })); setKey(newKey()); };
  const problem = useMemo(() => riskProblem(form), [form]);
  const setMeasure = (i, patch) => set({ measures: form.measures.map((m, j) => (j === i ? { ...m, ...patch } : m)) });
  const decisionChoices = useMemo(() => {
    const known = new Set(decisions.map((d) => d.id));
    return [...decisions, ...form.decision_ids.filter((id) => !known.has(id)).map((id) => ({ id, name: "Décision non visible avec vos droits" }))];
  }, [decisions, form.decision_ids]);

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      const body = riskPayload(form);
      let data;
      if (editing) {
        ({ data } = await api.patch(`/governance/risks/${riskId}`, { ...(locked ? { links: body.links, restricted: body.restricted, decision_ids: body.decision_ids } : body), expected_version: source.version }));
        toast.success("Risque modifié");
      } else {
        ({ data } = await api.post("/governance/risks", body, { headers: { "Idempotency-Key": key } }));
        toast.success("Risque consigné");
      }
      navigate(`/gouvernance/risques/${data.id}`);
    } catch (e) {
      setError(governanceError(e, "Enregistrement impossible. Réessayez."));
    } finally {
      setBusy(false);
    }
  };

  if (phase === "loading") return <div className="space-y-3"><Skeleton className="h-8 w-64" /><Skeleton className="h-48 w-full" /></div>;
  if (phase === "error") {
    return (
      <Card className="p-6 text-center space-y-2" data-testid="risk-form-error">
        <p className="text-sm text-slate-700">Ce risque est introuvable ou vous n'y avez pas accès.</p>
        <Button asChild variant="outline"><Link to="/gouvernance/risques">Retour aux risques</Link></Button>
      </Card>
    );
  }
  const back = editing ? `/gouvernance/risques/${riskId}` : "/gouvernance/risques";
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="risk-form">
      <div>
        <Link to={back} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900"><ArrowLeft size={14} />Retour</Link>
        <h1 className="font-display font-bold text-2xl text-slate-900 mt-1">{editing ? `Modifier ${source.number}` : "Nouveau risque"}</h1>
        <p className="text-sm text-slate-500">Décrivez ce qui pourrait arriver, évaluez-le et nommez celui ou celle qui en répond. Le niveau se calcule tout seul.</p>
      </div>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_20rem] items-start">
        <div className="space-y-4 min-w-0">
          {locked && <p className="rounded-md bg-amber-50 text-amber-900 text-sm px-3 py-2" data-testid="risk-locked-note">Ce risque est clos : rouvrez-le pour modifier son contenu. Les liens restent ajustables.</p>}
          <Section title="Le risque">
            <div className="space-y-1.5"><Label htmlFor="rk-title">Titre</Label>
              <Input id="rk-title" value={form.title} maxLength={200} onChange={(e) => set({ title: e.target.value })} className="h-10" disabled={locked} data-testid="risk-title" /></div>
            <div className="space-y-1.5"><Label>Catégorie</Label>
              <Select value={form.category || undefined} onValueChange={(category) => set({ category })} disabled={locked}>
                <SelectTrigger className="h-10 sm:w-64" aria-label="Catégorie" data-testid="risk-category"><SelectValue placeholder="Choisir une catégorie" /></SelectTrigger>
                <SelectContent>{RISK_CATEGORIES.map(([id, label]) => <SelectItem key={id} value={id}>{label}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="space-y-1.5"><Label htmlFor="rk-desc">Description</Label>
              <Textarea id="rk-desc" rows={4} maxLength={4000} value={form.description} onChange={(e) => set({ description: e.target.value })} disabled={locked} data-testid="risk-description" placeholder="Que pourrait-il se passer, et pourquoi ?" /></div>
          </Section>
          <Section title="Évaluation" hint="Impact × probabilité donne le niveau : faible, moyen, élevé ou critique.">
            <div className="grid gap-3 sm:grid-cols-2">
              <ScaleSelect label="Impact" labels={IMPACT_LABELS} value={form.impact} onChange={(impact) => set({ impact })} disabled={locked} testid="risk-impact" />
              <ScaleSelect label="Probabilité" labels={PROBABILITY_LABELS} value={form.probability} onChange={(probability) => set({ probability })} disabled={locked} testid="risk-probability" />
              <div className="space-y-1.5"><Label>Propriétaire</Label>
                <Select value={form.owner_id || undefined} onValueChange={(owner_id) => set({ owner_id })} disabled={locked}>
                  <SelectTrigger className="h-10" aria-label="Propriétaire" data-testid="risk-owner"><SelectValue placeholder="Choisir une personne" /></SelectTrigger>
                  <SelectContent>{owners.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
                </Select></div>
              <div className="space-y-1.5"><Label>Revue</Label>
                <Select value={String(form.review_months)} onValueChange={(v) => set({ review_months: Number(v) })} disabled={locked}>
                  <SelectTrigger className="h-10" aria-label="Périodicité de la revue" data-testid="risk-period"><SelectValue /></SelectTrigger>
                  <SelectContent>{REVIEW_PERIODS.map(([n, label]) => <SelectItem key={n} value={String(n)}>{label}</SelectItem>)}</SelectContent>
                </Select></div>
            </div>
          </Section>
          <Section title="Mesures" hint="Ce qui est fait ou prévu pour réduire le risque. Une mesure qui demande du travail devient une tâche, depuis la fiche du risque.">
            <div className="space-y-2">
              {form.measures.map((m, i) => (
                <div key={m.id || i} className="flex gap-2" data-testid={`measure-${i}`}>
                  <Input value={m.text} maxLength={500} onChange={(e) => setMeasure(i, { text: e.target.value })} placeholder={`Mesure ${i + 1}`} className="h-10" disabled={locked} aria-label={`Mesure ${i + 1}`} data-testid={`measure-text-${i}`} />
                  {!locked && <Button type="button" variant="ghost" className="h-10 w-10 p-0 shrink-0" onClick={() => set({ measures: form.measures.filter((_, j) => j !== i) })} aria-label={`Retirer la mesure ${i + 1}`}><Trash2 size={15} /></Button>}
                </div>
              ))}
              {!locked && form.measures.length < 20 && <Button type="button" variant="outline" size="sm" onClick={() => set({ measures: [...form.measures, emptyMeasure()] })} data-testid="measure-add"><Plus size={14} className="mr-1.5" />Ajouter une mesure</Button>}
            </div>
          </Section>
          <Section title="Décisions et liens" hint="Les décisions prises à son sujet, et ce qu'il concerne. Rien n'est modifié dans les autres modules.">
            <div className="space-y-1.5"><Label>Décisions liées</Label>
              <PeopleMultiSelect value={form.decision_ids} onChange={(decision_ids) => set({ decision_ids })} people={decisionChoices} placeholder="Choisir des décisions" testid="risk-decisions" /></div>
            <LinkList links={form.links} openable={false} onRemove={(l) => set({ links: form.links.filter((x) => x !== l) })} />
            <LinkPicker value={form.links} onChange={(links) => set({ links })} />
          </Section>
        </div>
        <div className="space-y-4 lg:sticky lg:top-4">
          <Card className="p-4">
            <label className="flex items-start justify-between gap-3 cursor-pointer">
              <span><span className="block text-sm font-medium text-slate-900">Risque restreint</span><span className="block text-xs text-slate-500">Visible seulement de son créateur, de son propriétaire et des personnes qui voient tout.</span></span>
              <Switch checked={form.restricted} onCheckedChange={(restricted) => set({ restricted })} aria-label="Risque restreint" data-testid="risk-restricted-switch" />
            </label>
          </Card>
          <Card className="p-4 space-y-2 text-sm text-slate-600">
            <h3 className="font-semibold text-slate-900">Ce qui se passe ensuite</h3>
            <p>Le risque revient dans « À revoir » à la date prévue. « Marquer comme revu » confirme ou révise l'évaluation ; l'ancienne reste dans l'historique.</p>
            <p>On peut aussi l'accepter (on vit avec) ou le clore (il a disparu), toujours avec un motif.</p>
          </Card>
        </div>
      </div>
      {error && <p className="text-sm text-red-600" role="alert" data-testid="risk-form-submit-error">{error}</p>}
      <div className="flex flex-wrap gap-2 justify-end">
        <Button asChild variant="outline" type="button"><Link to={back}>Annuler</Link></Button>
        <Button type="submit" disabled={busy || (!locked && !!problem)} title={(!locked && problem) || undefined} data-testid="risk-submit">
          {busy && <Loader2 size={14} className="mr-1.5 animate-spin" />}{editing ? "Enregistrer" : "Consigner le risque"}
        </Button>
      </div>
    </form>
  );
}
